<template>
    <v-app>
        <v-container fluid>
            <template v-if="$insertAccess($route.path)">
                <v-form @submit.prevent="submit">
                    <v-row>
                        <v-col cols="12">
                            <v-expansion-panels v-model="panel" >
                                <v-expansion-panel>
                                    <v-expansion-panel-title color="blue-grey-lighten-4" class="panel-header"><span>{{ isEntryMode ? "Attendance Upload" : "Update Attendance" }}</span></v-expansion-panel-title>
                                    <v-expansion-panel-text> 
                                        <v-row class="pt-2 px-2 justify-space-around align-end">
                                            <v-col cols="12" md="4">
                                                <v-date-input
                                                label="Start Date"
                                                density="compact"
                                                prepend-icon=""
                                                prepend-inner-icon="mdi-calendar"
                                                variant="outlined"
                                                bg-color="grey-lighten-4"
                                                v-model="start_date.value.value"
                                                :error-messages="start_date.errorMessage.value"
                                                autocomplete="off"
                                                clearable
                                                >
                                                    <template #label>
                                                        Start Date <span class="text-red-accent-4">*</span>
                                                    </template>
                                                </v-date-input>
                                            </v-col>

                                            <v-col cols="12" md="4">
                                                <v-date-input
                                                label="End Date"
                                                density="compact"
                                                prepend-icon=""
                                                prepend-inner-icon="mdi-calendar"
                                                variant="outlined"
                                                bg-color="grey-lighten-4"
                                                v-model="end_date.value.value"
                                                :error-messages="end_date.errorMessage.value"
                                                autocomplete="off"
                                                clearable
                                                >
                                                    <template #label>
                                                        End Date <span class="text-red-accent-4">*</span>
                                                    </template>
                                                </v-date-input>
                                            </v-col>

                                            <v-col cols="12" md="4">
                                                <v-file-input
                                                v-model="file.value.value"
                                                label="Upload Excel File"
                                                density="compact"
                                                prepend-icon=""
                                                prepend-inner-icon="mdi-paperclip"
                                                variant="outlined"
                                                bg-color="grey-lighten-4"
                                                accept=".xlsx, .xls, .csv"
                                                :error-messages="file.errorMessage.value"
                                                show-size
                                                clearable
                                                @update:model-value="handleFileUpload"
                                                >
                                                    <template #label>
                                                        Excel File <span class="text-red-accent-4">*</span>
                                                    </template>
                                                </v-file-input>
                                                <v-card v-if="file.value.value && file.value.value.length > 0" class="mt-2" variant="tonal" color="blue-lighten-5">
                                                    <v-card-text class="text-caption">
                                                        <v-icon size="small" class="text-blue">mdi-check-circle</v-icon>
                                                        File selected: <strong>{{ file.value.value[0]?.name }}</strong>
                                                    </v-card-text>
                                                </v-card>
                                            </v-col>
                                        </v-row>

                                        <v-divider class="my-4"></v-divider>

                                        <v-card-actions>
                                            <v-row class="pt-2">
                                                <v-col cols="12">
                                                    <v-layout class="justify-end">
                                                        <v-btn
                                                        class="bg-primary"
                                                        variant="flat"
                                                        @click="submitForm(1)"
                                                        :loading="loading"
                                                        >POST</v-btn>
                                                    </v-layout>
                                                </v-col>
                                            </v-row>
                                        </v-card-actions>

                                    </v-expansion-panel-text>
                                </v-expansion-panel>
                            </v-expansion-panels>
                        </v-col>
                    </v-row>
                </v-form>
            </template>
        </v-container>
    </v-app>
</template>

<script>

import {useStore} from 'vuex';
import { useField, useForm } from 'vee-validate';
import * as yup from 'yup';
import { ref } from 'vue';
import { toast } from 'vue3-toastify';
import { useRouter } from 'vue-router'; 
import formattedDate from '@/plugins/dateFormatter';

export default {
    components: {},
    data: () => ({
        itemsPerPage:10,
        panel: [0],
        search: "",
        data: {},
    }),

    watch: {},
    created() {
    },
    computed: {
    },

    methods: {
        resetForm() {
            this.data = {};
        },
        handleFileUpload(files) {
            if (files && files.length > 0) {
                const file = files[0];
                const validExtensions = ['xlsx', 'xls', 'csv'];
                const fileExtension = file.name.split('.').pop().toLowerCase();
                
                if (!validExtensions.includes(fileExtension)) {
                    toast.error('Please upload a valid Excel file (.xlsx, .xls, or .csv)');
                    this.file.value.value = [];
                    return;
                }
                
                if (file.size > 10 * 1024 * 1024) { // 10MB limit
                    toast.error('File size must be less than 10MB');
                    this.file.value.value = [];
                    return;
                }
            }
        }
    },

    setup() {
        const { handleSubmit, resetForm } = useForm({
            validationSchema: yup.object({
                start_date: yup.date().required().label('Start Date'),
                end_date: yup.date().required().label('End Date'),
                file: yup
                .mixed()
                .required("Excel file is required")
                .test("isExcel", "Only .xlsx files allowed", (value) => {
                    if (!value) return false;

                    const file = value instanceof File ? value : value[0];
                    if (!file) return false;
                    
                    return file.name.toLowerCase().endsWith(".xlsx");
                })


            })

        });

        const start_date = useField('start_date');
        const end_date = useField('end_date');
        const file = useField('file');
        
        const errorMessages = ref('');
        const loading = ref(false);
        const isEntryMode = ref(true);

        const store = useStore();
        const router = useRouter();

        const currentStatus = ref(0);

        const submitForm = (status) => {
            currentStatus.value = status;
            submit();
        };

        const submit = handleSubmit((values) => {
            loading.value = true;
            const formData = new FormData();
            
            values.start_date = formattedDate(start_date.value.value);
            values.end_date = formattedDate(end_date.value.value);

            formData.append('start_date', values.start_date);
            formData.append('end_date', values.end_date);
            
            // Append file
            if (file.value.value && file.value.value.length > 0) {
                formData.append('file', file.value.value[0]);
            }

            formData.append('status', currentStatus.value);
            console.log(values)
            store.dispatch("saveUpdateAttendanceUpload", values) 
                .then((response) => {
                    loading.value = false;
                    resetForm();
                    toast.success(response.data.message, {
                        timeout: 3000,
                    });
                })
                .catch((error) => { 
                    loading.value = false;
                    toast.error(error.response?.data?.message || 'An error occurred');
                })
        });

        return {
            submit,
            submitForm,
            start_date,
            end_date,
            file,
            errorMessages,
            isEntryMode,
            resetForm,
            loading
        }
    },
    mounted() {
        // if (this.$store.getters.getEditingAttendanceReconciliation) {
        //     const data = this.$store.getters.getEditingAttendanceReconciliation;
        //     this.$store.commit('SET_LOADING', { color: 'primary' });
            
        //     this.isEntryMode = false;

        //     this.attendance_reconciliation_id.value.value = data.id;
        //     this.attendance_date.value.value = data.date;
        //     this.employee_id.value.value = data.employee_id || null;
        //     this.actual_in.value.value = data.actual_in? data.actual_in.slice(0,5) : null;
        //     this.actual_out.value.value = data.actual_out? data.actual_out.slice(0,5) : null;
        //     this.in_remarks.value.value = data.in_remarks;
        //     this.out_remarks.value.value = data.out_remarks;

        //     this.$store.commit('UNSET_LOADING');
        // }
    },
    beforeUnmount() {
        this.$store.commit('UNSET_EDITING_ATTENDANCE_RECONCILIATION');
    }
}
</script>